
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth, routeForStep } from '../../store/auth';

const OTP_LEN = 6;

export default function VerifyEmail() {
    const user      = useAuth(s => s.user);
    const sendOtp   = useAuth(s => s.sendOtp);
    const verifyOtp = useAuth(s => s.verifyOtp);
    const [digits, setDigits] = useState(Array(OTP_LEN).fill(''));
    const [busy, setBusy]     = useState(false);
    const [resending, setResending] = useState(false);
    const [devOtp, setDevOtp] = useState(null);
    const refs = useRef([]);
    const nav  = useNavigate();

    useEffect(() => { refs.current[0]?.focus(); }, []);

    const setDigit = (i, v) => {
        const ch = v.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[i] = ch;
        setDigits(next);
        if (ch && i < OTP_LEN - 1) refs.current[i + 1]?.focus();
    };

    const onPaste = (e) => {
        const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LEN);
        if (!text) return;
        e.preventDefault();
        const next = Array(OTP_LEN).fill('');
        text.split('').forEach((c, i) => { next[i] = c; });
        setDigits(next);
        refs.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
    };

    const onKey = (i, e) => {
        if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    };

    const submit = async (e) => {
        e?.preventDefault();
        const code = digits.join('');
        if (code.length !== OTP_LEN) return toast.error(`Enter all ${OTP_LEN} digits`);
        setBusy(true);
        try {
            const { user: u } = await verifyOtp(code);
            toast.success('Email verified ✓');
            nav(routeForStep(u));
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    const resend = async () => {
        setResending(true);
        try {
            const r = await sendOtp();
            toast.success('New code sent');
            if (r.devOtp) setDevOtp(r.devOtp);
            setDigits(Array(OTP_LEN).fill(''));
            refs.current[0]?.focus();
        } catch {/* toasted */}
        finally { setResending(false); }
    };

    return (
        <div className="space-y-6">
            <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center mx-auto">
                    <Mail className="h-7 w-7" />
                </div>
                <h1 className="text-2xl font-extrabold mt-4">Verify your email</h1>
                <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
                    We sent a 6-digit code to <b>{user?.email || 'your email'}</b>. Enter it below to continue.
                </p>
            </div>

            <form onSubmit={submit} className="ag-card p-6 lg:p-8 space-y-5">
                <div className="flex justify-center gap-2" onPaste={onPaste}>
                    {digits.map((d, i) => (
                        <input
                            key={i}
                            ref={el => refs.current[i] = el}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={1}
                            value={d}
                            onChange={e => setDigit(i, e.target.value)}
                            onKeyDown={e => onKey(i, e)}
                            className="w-12 h-14 text-center font-heading text-2xl font-bold rounded-lg border-2 border-ink-300 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30 tabular-nums"
                        />
                    ))}
                </div>

               

                <button className="ag-btn-primary w-full justify-center" disabled={busy}>
                    {busy ? 'Verifying…' : 'Verify code'} <ArrowRight className="h-4 w-4" />
                </button>

                <div className="text-center">
                    <button type="button" onClick={resend} disabled={resending}
                        className="text-xs text-ink-500 hover:text-brand-teal-700 inline-flex items-center gap-1">
                        <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} /> Resend code
                    </button>
                </div>
            </form>

            <p className="text-[11px] text-ink-500 text-center">
                Codes expire in 10 minutes · 3 attempts before lock-out.
            </p>
        </div>
    );
}
